import { CorePalette, PaletteCategory, SemanticColorsMap, Theme, Themes, ThemeSemanticColors, ThemeStyles, ThemeTypography } from '@libs/types';
import { unzipSync } from 'fflate';

type StyleObj = {
    [key: string]: Record<string, unknown> | undefined;
};

const isObject = (val: unknown): val is Record<string, unknown> => !!val && typeof val === 'object' && !Array.isArray(val);

// Recursively merge `source` into `target`, returning a new object.
// When a key holds a plain object on both sides, its contents are merged
// recursively; otherwise the `source` value wins (arrays and scalars are
// treated as leaves, preserving last-write-wins behaviour).
const mergeDeep = (target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> => {
    const output: Record<string, unknown> = { ...target };
    for (const key of Object.keys(source)) {
        const sourceVal = source[key];
        const targetVal = target[key];
        if (isObject(sourceVal) && isObject(targetVal)) {
            output[key] = mergeDeep(targetVal, sourceVal);
        } else {
            output[key] = sourceVal;
        }
    }
    return output;
};

const mapTextToStyleObjectVal = (textObj: PaletteCategory, styleObj: StyleObj): Record<string, unknown> => {
    return Object.fromEntries(
        Object.entries(textObj).map(([key, value]) => {
            if (value && typeof value === 'string') {
                const path = value.split('.');
                let resolvedStyle: StyleObj | Record<string, unknown> | string = styleObj;
                if (key === 'font') {
                    resolvedStyle = path[path.length - 1];
                } else {
                    if (path) {
                        for (const segment of path) {
                            if (resolvedStyle && typeof resolvedStyle === 'object' && Object.hasOwn(resolvedStyle, segment)) {
                                resolvedStyle = resolvedStyle[segment] as Record<string, unknown>;
                            } else {
                                resolvedStyle = value;
                                break;
                            }
                        }
                    }
                }
                return [key, resolvedStyle];
            }
            if (value && typeof value === 'object') {
                value = mapTextToStyleObjectVal(value as PaletteCategory, styleObj);
            }
            return [key, value];
        }),
    );
};

const fillFonts = (fonts?: ThemeTypography['fonts']) => {
    if (fonts) {
        const newFonts = structuredClone(fonts);
        Object.entries(newFonts).forEach(([fontFamilyName, font]) => {
            Object.entries(font).forEach(([_fontType, value]) => {
                value.fontFamily = fontFamilyName;
            });
        });
        return newFonts;
    }
    return {};
};

const hasObject = (value: unknown) => {
    return !(!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length === 0);
};
const getLightDarkPalettes = (palette: Theme['palette']) => {
    let lightPalette: CorePalette = {},
        darkPalette: CorePalette = {};
    const hasLightTheme = hasObject(palette?.light);
    const hasDarkTheme = hasObject(palette?.dark);
    if (hasLightTheme) {
        lightPalette = palette?.light ?? {};
    } else if (!hasLightTheme && hasDarkTheme) {
        lightPalette = palette?.dark ?? {};
    }
    if (hasDarkTheme) {
        darkPalette = palette?.dark ?? {};
    } else if (!hasDarkTheme && hasLightTheme) {
        darkPalette = palette?.light ?? {};
    }
    return {
        lightPalette,
        darkPalette,
    };
};
const getPaletteWithColors = (lightPalette: CorePalette, darkPalette: CorePalette, colors: StyleObj) => {
    const light: CorePalette = {};
    const dark: CorePalette = {};
    for (const [key, value] of Object.entries(lightPalette)) {
        if (value && Object.keys(value).length > 0) {
            light[key] = mapTextToStyleObjectVal(value, colors);
        }
    }
    for (const [key, value] of Object.entries(darkPalette)) {
        if (value && Object.keys(value).length > 0) {
            dark[key] = mapTextToStyleObjectVal(value, colors);
        }
    }
    return { light, dark };
};

const getStylesWithTypography = (styles: ThemeStyles, typography: StyleObj) => {
    const result: ThemeStyles = {};
    for (const [key, value] of Object.entries(styles)) {
        if (value && Object.keys(value).length > 0) {
            result[key] = mapTextToStyleObjectVal(value, typography);
        }
    }
    return result;
};

const COLOR_REFERENCE_PREFIX = 'colors.';

// Resolves `colors.<name>.<shade>` against `theme.colors`. A reference that does not resolve to a string is
// dropped rather than passed through: the raw text in a custom property would make every `var()` using it
// invalid at computed-value time, whereas a missing key falls back to the package CSS. Non-reference values
// (`#fff`, `rgba(…)`) are kept as literal colors.
const resolveSemanticColorsMap = (map: SemanticColorsMap | undefined, colors: Theme['colors']): SemanticColorsMap => {
    const resolved: SemanticColorsMap = {};
    if (!isObject(map)) return resolved;
    for (const [token, reference] of Object.entries(map)) {
        if (typeof reference !== 'string') continue;
        if (!reference.startsWith(COLOR_REFERENCE_PREFIX)) {
            resolved[token] = reference;
            continue;
        }
        let value: unknown = colors;
        for (const segment of reference.slice(COLOR_REFERENCE_PREFIX.length).split('.')) {
            value = isObject(value) && Object.hasOwn(value, segment) ? value[segment] : undefined;
        }
        if (typeof value === 'string') resolved[token] = value;
    }
    return resolved;
};

// Each scheme is resolved on its own — unlike the palette, a missing side is NOT copied from the other one,
// so the package CSS keeps supplying that scheme.
const resolveSemanticColors = (semanticColors: ThemeSemanticColors | undefined, colors: Theme['colors']): ThemeSemanticColors => ({
    light: resolveSemanticColorsMap(semanticColors?.light, colors),
    dark: resolveSemanticColorsMap(semanticColors?.dark, colors),
});

/**
 * Version of the shape produced by {@link processThemeJson} + {@link mergeStylesIntoPalette}. The processed theme is
 * cached in localStorage under its `themeHash` (see `resolveScenarioTheme`), and a record with an older or missing
 * version is a cache miss even when the hash still matches — otherwise users keep a copy built by the previous
 * player. **Bump it whenever either function starts producing a different shape.**
 *
 * - 2 — the processed theme carries `semanticColors`.
 */
export const PROCESSED_THEME_VERSION = 2;

export const processThemeJson = (jsonData: Theme): Theme => {
    const { lightPalette, darkPalette } = getLightDarkPalettes(jsonData?.palette);
    const fonts = fillFonts(jsonData?.typography?.fonts);
    const { light, dark } = getPaletteWithColors(lightPalette, darkPalette, { colors: jsonData.colors });
    const styles = getStylesWithTypography(jsonData?.styles ?? {}, { typography: jsonData.typography ?? {} });

    return {
        themeName: jsonData.themeName ?? jsonData.name,
        isDefault: jsonData.isDefault ?? false,
        themeHash: jsonData.themeHash,
        colors: jsonData.colors,
        typography: {
            fonts,
        },
        palette: {
            light,
            dark,
        },
        styles,
        semanticColors: jsonData.semanticColors ? resolveSemanticColors(jsonData.semanticColors, jsonData.colors) : undefined,
    };
};

// Bake `theme.styles` into every palette side so widgets can read a fully
// resolved palette without merging styles at render time. Palette and styles
// share the same key structure; for every style key we deep-merge it into the
// matching palette entry on both the light and dark sides (styles win on
// conflict). When a style key has no matching palette entry it is added to all
// sides, so a style is never lost just because the palette omits its slot.
export const mergeStylesIntoPalette = (theme: Theme): Theme => {
    const styles = theme.styles ?? {};
    if (!theme.palette || Object.keys(styles).length === 0) return theme;

    const mergeSide = (side: CorePalette): CorePalette => {
        const merged: CorePalette = { ...side };
        for (const [styleKey, styleVal] of Object.entries(styles)) {
            merged[styleKey] = mergeDeep((merged[styleKey] ?? {}) as Record<string, unknown>, (styleVal ?? {}) as Record<string, unknown>) as PaletteCategory;
        }
        return merged;
    };

    return {
        themeName: theme.themeName,
        isDefault: theme.isDefault,
        themeHash: theme.themeHash,
        colors: theme.colors,
        palette: {
            light: mergeSide(theme.palette.light ?? {}),
            dark: mergeSide(theme.palette.dark ?? {}),
        },
        semanticColors: theme.semanticColors,
    };
};

const isZipBuffer = (buffer: ArrayBuffer): boolean => {
    const bytes = new Uint8Array(buffer, 0, 2);
    return bytes[0] === 0x50 && bytes[1] === 0x4b;
};

const parseZipBuffer = (buffer: ArrayBuffer): Theme => {
    const files = unzipSync(new Uint8Array(buffer));
    const jsonFileName = Object.keys(files).find((name) => name.endsWith('.json'));
    if (!jsonFileName) throw new Error('No JSON file found in ZIP archive');
    const text = new TextDecoder().decode(files[jsonFileName]);
    return JSON.parse(text);
};

const fetchThemeFromLink = async (url: string): Promise<Theme | null> => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            console.error(`fetchThemeFromLink: HTTP ${response.status} for ${url}`);
            return null;
        }
        const buffer = await response.arrayBuffer();
        if (isZipBuffer(buffer)) {
            return parseZipBuffer(buffer);
        }
        return JSON.parse(new TextDecoder().decode(buffer));
    } catch (e) {
        console.error('fetchThemeFromLink:', e);
        return null;
    }
};

export const resolveTheme = async (theme: Theme): Promise<Theme> => {
    if (!theme.themeLink) return theme;
    const { themeLink, ...themeWithoutLink } = theme;
    if (typeof themeLink === 'object') return { ...themeWithoutLink, ...themeLink };
    return (await fetchThemeFromLink(themeLink)) ?? theme;
};

export const findTheme = (themes: Themes, pageThemeName?: string) => {
    if (themes.length === 0) return undefined;

    if (pageThemeName) {
        const byName = themes.find((t) => t.themeName === pageThemeName);
        if (byName) return byName;
    }

    return themes.find((t) => t.isDefault);
};
